<script>

//////////////////////////////////////////////////////////////////////////////////////////////////////
//
//  Shine.vue
//  author Alex Lowe
//
//  This is meant to be a object to add to any wrapper
//
//    <div v-if="myDataService">
//      <template v-html="myDataService.text"></template>
//      <Shine v-if="!myDataService"/>
//    </div>
//  </Shine>
//
//  It will have a default minimum height of 100px which you can adjust with the min prop
//
///////////////////////////////////////////////////////////////////////////////////////////////////////

import { defineComponent, computed } from 'vue'
import "@kit/assets/css/superbox-core-x.css"

export default defineComponent({
  name: "Shine",
  props: {
    //the class of the loading shine. options are "light" or "dark"
    "shine": { required:false, default:"light"},

    //sw/sh explicit width and height for the shine
    "w": {required:false, default:null },
    "h": {required:false, default:null },
    //ssize: use classes that set the width and/or height
    //for example, the handy .w-800px-50pct-70pct-95pct
    "size": { required:false, default:null },

    //The color of the shine
    "color": { required:false, default:null},

    //the min height. This wrapper is to be used with content that is loading. We don't know what size it 
    //will be so we have to just guess
    "min": { required:false, default:"100%" },

    //the tag to use for the wrapper
    "tag": { required:false, default:"div"},

    //the amount of padding for the inner shine
    "padding": { required:false, default:null },

    //the optional style to merge in for the outer wrapper
    "outerStyle": {required:false, default:""},
  },

  setup(props) {

    const outerStyle_ = computed(() => {
      const color = props.color ? ` background:${props.color};` : ""
      const padding = usePadding_.value ? ` padding:${props.padding};` : ""
      return `width:100%; height:${props.min}; ${props.outerStyle};${padding}${color}` 
    })

    const outerClass_ = computed(() => {
      return usePadding_.value ? "sb sb-v sb-greedy-1" : "sb sb-v sb-alt2 sb-align-c"
    })

    const shineClass_ = computed(() => {
      let size = ""

      //a lot of time we just want to give it a height and call it a day
      //in that case, use this default responsive width class.
      if(!usePadding_.value) {
        if(!props.w && !props.size) {
          size = " w-800px-50pct-70pct-95pct"
        } else 
        if(props.size) {
          size = ` ${props.size}`
        }
      } 

      const sbClass = !usePadding_.value ? `sb sb-explicit${size} ` : "sb sb-greedy"
      const shine = props.shine == "light" ? " loading-shine-transparent-over-light" : " loading-shine-transparent-over-dark"
      
      return `${sbClass}${shine}${size}`
    })

    const shineStyle_ = computed(() => {
      const width = props.w ? `width:${props.w}; ` : ''
      const height = props.h ? `height:${props.h};` : ''
      return usePadding_.value ? "" : `${width}${height}`
    })

    const usePadding_ = computed(() => {
      return !!props.padding
    })

    return { shineClass_, shineStyle_, usePadding_, outerStyle_, outerClass_ }
  }
  
});
</script>

<style>

/*ICS styles*/
.loading-shine {
  background-repeat: no-repeat;
  background-size: 800px 100%; 
  display: inline-block;  
  animation-duration: 1s;
  animation-fill-mode: forwards; 
  animation-iteration-count: infinite;
  animation-name: placeholderShimmer;
  animation-timing-function: linear;
}

.loading-shine-transparent-over-light {
  background-repeat: no-repeat;
  background-size: 800px 100%; 
  display: inline-block;  
  animation-duration: 1s;
  animation-fill-mode: forwards; 
  animation-iteration-count: infinite;
  animation-name: placeholderShimmer;
  animation-timing-function: linear;
}

.loading-shine-transparent-over-dark {
  background-repeat: no-repeat;
  background-size: 800px 100%; 
  display: inline-block;
  animation-duration: 1s;
  animation-fill-mode: forwards; 
  animation-iteration-count: infinite;
  animation-name: placeholderShimmer2;
  animation-timing-function: linear;
} 

@keyframes placeholderShimmer {
  0% {
    background-position: -468px 0;
  }
  
  100% {
    background-position: 468px 0; 
  }
}
@keyframes placeholderShimmer2 {
  0% {
    background-position: -936px 0;
  }
  
  100% {
    background-position: 936px 0; 
  }
}

</style>

<template>

  <component :is="tag" :style="outerStyle_" :class="outerClass_">
    <div :style="shineStyle_" :class="shineClass_"></div>
  </component>

</template>